import type {
  AlarmsResponse,
  BudgetsResponse,
  CostsResponse,
  DeploymentsResponse,
  HealthResponse,
  MetricEnv,
  MetricsResponse,
} from "./types.js";

import { applyDemo } from "../lib/demo.js";

const TOKEN_KEY = "jammsante_monitoring_token";

export function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  sessionStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  sessionStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function request<T>(path: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, { headers: authHeaders() });
  } catch (err) {
    throw new ApiError(0, err instanceof Error ? err.message : "network error");
  }

  if (res.status === 401) {
    throw new ApiError(401, "unauthorized");
  }

  if (!res.ok) {
    let message = `request failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) message = body.error;
    } catch {
      // non-JSON body; keep the generic message.
    }
    throw new ApiError(res.status, message);
  }

  return applyDemo(path, (await res.json()) as T);
}

export function getHealth(): Promise<HealthResponse> {
  return request<HealthResponse>("/api/health");
}

export function getCosts(): Promise<CostsResponse> {
  return request<CostsResponse>("/api/costs");
}

export function getAlarms(): Promise<AlarmsResponse> {
  return request<AlarmsResponse>("/api/alarms");
}

export function getMetrics(env: MetricEnv): Promise<MetricsResponse> {
  return request<MetricsResponse>(`/api/metrics/${env}`);
}

export function getBudgets(): Promise<BudgetsResponse> {
  return request<BudgetsResponse>("/api/budgets");
}

export function getDeployments(): Promise<DeploymentsResponse> {
  return request<DeploymentsResponse>("/api/deployments");
}

/** Validate a token by calling an authenticated endpoint; returns true if accepted. */
export async function verifyToken(): Promise<boolean> {
  try {
    await getCosts();
    return true;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return false;
    // Any non-auth error (network, 5xx, degraded section) still means the token was accepted.
    return true;
  }
}

import type { AccountKey, AlarmHistoryResponse } from "./types.js";

export function getAlarmHistory(account: AccountKey, name: string): Promise<AlarmHistoryResponse> {
  const params = new URLSearchParams({ account, name });
  return request<AlarmHistoryResponse>(`/api/alarms/history?${params.toString()}`);
}

import type { PipelineDetailResponse, PipelinesResponse, UptimeResponse } from "./types.js";

export function getUptime(): Promise<UptimeResponse> {
  return request<UptimeResponse>("/api/uptime");
}

export function getPipelines(): Promise<PipelinesResponse> {
  return request<PipelinesResponse>("/api/pipelines");
}

export function getPipelineDetail(app: string): Promise<PipelineDetailResponse> {
  return request<PipelineDetailResponse>(`/api/pipelines/${encodeURIComponent(app)}`);
}
